/**
 * MyProjectsPage - Lista dei progetti dell'utente
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Description as DocIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon
} from '@mui/icons-material';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

interface Project {
  project_id: string;
  title: string;
  acronym: string;
  program_type: string;
  duration: number;
  budget: number;
  status: string;
  final_document_filename: string | null;
  created_at: string;
  updated_at: string;
}

interface ProjectsByUser {
  [userEmail: string]: Project[];
}

interface MyProjectsPageProps {
  onBack: () => void;
}

const MyProjectsPage: React.FC<MyProjectsPageProps> = ({ onBack }) => {
  const { token } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsByUser, setProjectsByUser] = useState<ProjectsByUser>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load projects');
      }

      const data = await response.json();
      
      // Check if user is admin
      if (data.is_admin && data.projects_by_user) {
        setIsAdmin(true);
        setProjectsByUser(data.projects_by_user);
      } else {
        setIsAdmin(false);
        setProjects(data.projects || []);
      }
    } catch (err: any) {
      setError(err.message || 'Error loading projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (projectId: string, filename: string | null = null) => {
    try {
      setDownloading(projectId);
      const downloadUrl = `${API_URL}/api/project/${projectId}/download-docx`;
      console.log(`📥 Attempting to download document for project ${projectId}`);
      console.log(`📥 URL: ${downloadUrl}`);
      console.log(`📥 API_URL: ${API_URL}`);
      
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log(`📥 Response status: ${response.status}`);
      console.log(`📥 Response headers:`, Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Download failed:', response.status, errorText);
        
        if (response.status === 404) {
          // Check if it's HTML (404 page) or JSON error
          if (errorText.trim().startsWith('<')) {
            alert('Endpoint not found. Please check if the backend is deployed with the latest version.');
          } else {
            alert('Document not found. Please export the project first.');
          }
          return;
        }
        throw new Error(`Failed to download document: ${response.status} - ${errorText.substring(0, 100)}`);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let downloadFilename = filename || `Erasmus_Project_${projectId.substring(0, 8)}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) {
          downloadFilename = filenameMatch[1];
        }
      }

      console.log(`✅ Downloading file: ${downloadFilename}`);

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('✅ Download completed successfully');
    } catch (err: any) {
      console.error('❌ Download error:', err);
      alert(`Error downloading document: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return 'success';
      case 'generating':
        return 'warning';
      case 'draft':
        return 'info';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderProjectItem = (project: Project) => (
    <Paper
      key={project.project_id}
      elevation={2}
      sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}
    >
      <ListItem
        secondaryAction={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={project.status}
              color={getStatusColor(project.status) as any}
              size="small"
              sx={{ mr: 1 }}
            />
            <Tooltip title={project.final_document_filename ? "Download DOCX" : "Try to download (may not be available)"}>
              <IconButton
                edge="end"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownload(project.project_id, project.final_document_filename);
                }}
                disabled={downloading === project.project_id}
                color="primary"
              >
                {downloading === project.project_id ? (
                  <CircularProgress size={24} />
                ) : (
                  <DownloadIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        }
      >
        <ListItemButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('🖱️ Clicked on project:', project.project_id, 'has filename:', project.final_document_filename);
            handleDownload(project.project_id, project.final_document_filename);
          }}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" component="div">
                  {project.title}
                </Typography>
                {project.acronym && (
                  <Chip
                    label={project.acronym}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            }
            secondary={
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Program:</strong> {project.program_type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <CalendarIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    {project.duration} months
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <EuroIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    €{project.budget.toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Created: {formatDate(project.created_at)} • Updated: {formatDate(project.updated_at)}
                </Typography>
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)', py: 4 }}>
      <Container maxWidth="lg">
        <Paper elevation={8} sx={{ p: 4, background: 'rgba(255, 255, 255, 0.98)', borderRadius: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ flexGrow: 1, color: '#003399', fontWeight: 700 }}>
              My Projects
            </Typography>
          </Box>

          {/* Loading */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Projects List */}
          {!loading && !error && (
            <>
              {isAdmin ? (
                // Admin view: Show all projects grouped by user
                <>
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ color: 'warning.dark', fontWeight: 700 }}>
                      🔐 ADMIN VIEW - All Projects
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      Showing all projects from all users, grouped by email
                    </Typography>
                  </Box>
                  {Object.keys(projectsByUser).length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <DocIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No projects found
                      </Typography>
                    </Box>
                  ) : (
                    Object.entries(projectsByUser).map(([userEmail, userProjects]) => (
                      <Box key={userEmail} sx={{ mb: 4 }}>
                        <Paper elevation={3} sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
                          <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 700 }}>
                            👤 {userEmail}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {userProjects.length} project{userProjects.length !== 1 ? 's' : ''}
                          </Typography>
                        </Paper>
                        <List>
                          {userProjects.map((project) => renderProjectItem(project))}
                        </List>
                      </Box>
                    ))
                  )}
                </>
              ) : (
                // Regular user view: Show only own projects
                <>
                  {projects.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <DocIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No projects yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start creating your first Erasmus+ project!
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        You have {projects.length} project{projects.length !== 1 ? 's' : ''}
                      </Typography>
                      <List>
                        {projects.map((project) => renderProjectItem(project))}
                      </List>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default MyProjectsPage;

